import { useCallback, useState } from 'react';
import { BannerCanvas } from './BannerCanvas';
import { BannerToolbar } from './BannerToolbar';
import { BannerSidebar } from './BannerSidebar';
import { BannerNFTSearch } from './BannerNFTSearch';
import { Button, ImagePreviewModal } from '../common';
import { useBannerStore } from '../../stores';
import { downloadImage, isBackgroundLayer, isCharacterLayer, isFrameLayer } from '../../utils';

interface BannerBuilderProps {
  onClose?: () => void;
}

export function BannerBuilder({ onClose }: BannerBuilderProps) {
  const { canvasWidth, canvasHeight, backgroundImage, backgroundColor, elements } =
    useBannerStore();

  // Preview modal state for Telegram fallback
  const [previewImage, setPreviewImage] = useState<{ url: string; filename: string } | null>(null);

  const handleExport = useCallback(async () => {
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = canvasWidth;
    exportCanvas.height = canvasHeight;

    const ctx = exportCanvas.getContext('2d');
    if (!ctx) {
      console.error('Could not get canvas context');
      return;
    }

    const loadImage = (url: string): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });
    };

    if (backgroundImage) {
      try {
        const bgImg = await loadImage(backgroundImage);
        const imgAspect = bgImg.width / bgImg.height;
        const canvasAspect = canvasWidth / canvasHeight;

        let drawWidth: number;
        let drawHeight: number;
        let drawX: number;
        let drawY: number;

        if (imgAspect > canvasAspect) {
          drawHeight = canvasHeight;
          drawWidth = drawHeight * imgAspect;
          drawX = (canvasWidth - drawWidth) / 2;
          drawY = 0;
        } else {
          drawWidth = canvasWidth;
          drawHeight = drawWidth / imgAspect;
          drawX = 0;
          drawY = (canvasHeight - drawHeight) / 2;
        }

        ctx.drawImage(bgImg, drawX, drawY, drawWidth, drawHeight);
      } catch (err) {
        console.error('Failed to load background image:', err);
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      }
    } else {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    }

    const sortedElements = [...elements].sort((a, b) => a.zIndex - b.zIndex);

    // Helper function to draw full-canvas layer with vertical offset
    const drawFullCanvasLayer = (
      layerImg: HTMLImageElement,
      verticalOffset: number = 0
    ) => {
      const imgAspect = layerImg.width / layerImg.height;
      const canvasAspect = canvasWidth / canvasHeight;

      let drawWidth: number;
      let drawHeight: number;
      let drawX: number;

      if (imgAspect > canvasAspect) {
        drawHeight = canvasHeight;
        drawWidth = drawHeight * imgAspect;
        drawX = (canvasWidth - drawWidth) / 2;
      } else {
        drawWidth = canvasWidth;
        drawHeight = drawWidth / imgAspect;
        drawX = 0;
      }

      // Apply vertical offset as percentage of overflow
      const verticalOverflow = drawHeight - canvasHeight;
      const yOffset = (verticalOffset / 100) * (verticalOverflow / 2);
      const drawY = (canvasHeight - drawHeight) / 2 + yOffset;

      ctx.drawImage(layerImg, drawX, drawY, drawWidth, drawHeight);
    };

    const shouldRenderNFTBackgrounds = !backgroundImage;

    // PASS 1: Draw ALL background layers first (in z-order)
    if (shouldRenderNFTBackgrounds) {
      for (const element of sortedElements) {
        if (!element.useBackgroundLayer) continue;

        const backgroundLayers = element.nftData.layerAssets.filter(isBackgroundLayer);

        for (const layer of backgroundLayers) {
          try {
            const layerImg = await loadImage(layer.uri);
            drawFullCanvasLayer(layerImg, element.backgroundVerticalOffset || 0);
          } catch (err) {
            console.error('Failed to load background layer for export:', layer.id, err);
          }
        }
      }
    }

    // PASS 2: Draw ALL character layers (in z-order)
    for (const element of sortedElements) {
      ctx.save();

      const layers = element.nftData.layerAssets;

      ctx.translate(element.position.x, element.position.y);
      ctx.rotate((element.rotation * Math.PI) / 180);

      if (layers.length > 0) {
        const characterLayers = layers.filter((layer) => {
          if (element.selectedLayers.length > 0) {
            return element.selectedLayers.includes(layer.id);
          }

          // Background/frame handled in pass 1 / pass 3
          return isCharacterLayer(layer);
        });

        let refWidth = 512;
        let refHeight = 512;
        try {
          const refImg = await loadImage(element.nftData.imageUrl);
          refWidth = refImg.width;
          refHeight = refImg.height;
        } catch {
          // Use default dimensions if composite image fails to load
        }

        for (const layer of characterLayers) {
          try {
            const layerImg = await loadImage(layer.uri);
            const scaledWidth = refWidth * element.scale;
            const scaledHeight = refHeight * element.scale;
            ctx.drawImage(
              layerImg,
              -scaledWidth / 2,
              -scaledHeight / 2,
              scaledWidth,
              scaledHeight
            );
          } catch (err) {
            console.error('Failed to load layer for export:', layer.id, err);
          }
        }
      } else {
        // Fallback: draw full NFT image at position and scale
        try {
          const img = await loadImage(element.nftData.imageUrl);
          const scaledWidth = img.width * element.scale;
          const scaledHeight = img.height * element.scale;
          ctx.drawImage(img, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
        } catch (err) {
          console.error('Failed to load NFT image for export:', err);
        }
      }

      ctx.restore();
    }

    // PASS 3: Draw ALL frame layers last (in z-order)
    for (const element of sortedElements) {
      if (!element.useFrameLayer) continue;

      const frameLayers = element.nftData.layerAssets.filter(isFrameLayer);

      for (const layer of frameLayers) {
        try {
          const layerImg = await loadImage(layer.uri);
          drawFullCanvasLayer(layerImg, element.frameVerticalOffset || 0);
        } catch (err) {
          console.error('Failed to load frame layer for export:', layer.id, err);
        }
      }
    }

    // Convert to PNG blob and download using smart download utility
    exportCanvas.toBlob(async (blob) => {
      if (blob) {
        const filename = `nft_banner_${canvasWidth}x${canvasHeight}.png`;

        await downloadImage(blob, filename, {
          onPreviewRequired: (dataUrl, fname) => {
            setPreviewImage({ url: dataUrl, filename: fname });
          },
        });
      }
    }, 'image/png');
  }, [canvasWidth, canvasHeight, backgroundImage, backgroundColor, elements]);

  return (
    <div className="flex flex-col h-full">
      <BannerToolbar onClose={onClose} />

      <div className="flex flex-col lg:flex-row gap-5 flex-1 min-h-0">
        {/* Canvas area */}
        <div className="flex-1 min-w-0 flex flex-col items-center justify-start overflow-y-auto">
          <div className="w-full max-w-[900px] px-2 sm:px-0">
            <BannerCanvas />
          </div>
          <div className="mt-5 flex-shrink-0">
            <Button variant="primary" onClick={handleExport} className="px-8">
              Export PNG
            </Button>
          </div>
        </div>
        {/* Sidebar */}
        <div className="w-full lg:w-80 flex-shrink-0 lg:overflow-y-auto">
          <BannerSidebar />
        </div>
      </div>

      <BannerNFTSearch />

      {/* Image Preview Modal for Telegram fallback */}
      <ImagePreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewImage?.url || ''}
        filename={previewImage?.filename || ''}
      />
    </div>
  );
}
